import { Controller } from "@hotwired/stimulus"

// Catalog filter bar: pick what to filter by (artist / label / organization),
// search it, then navigate to the catalog with that filter applied. Filters
// stack, so the selected value is merged into the current query string rather
// than replacing it.
export default class extends Controller {
  static targets = [
    "searchContainer", "searchButtonText", "searchInput", "results", "entityContainer"
  ]

  static values = {
    startTyping: String,
    searching: String,
    noResults: String,
    error: String,
    albumsLabel: String
  }

  // Endpoint and query parameter per filter type. `param` is what ends up in
  // the catalog URL; `value` is which field of the result feeds it.
  static FILTERS = {
    artist: { url: "/albums/filter_artists", param: "artist_id", value: "id" },
    label: { url: "/albums/label_search", param: "label_name", value: "label_name" },
    organization: { url: "/organizations/search", param: "organization_id", value: "id" }
  }

  connect() {
    this.currentFilterType = null
  }

  selectEntity(event) {
    event.preventDefault()

    const filterType = event.currentTarget.dataset.filterType
    const placeholder = event.currentTarget.dataset.searchPlaceholder

    this.currentFilterType = filterType

    this.closeDropdownFor(this.entityContainerTarget)

    this.searchContainerTarget.classList.remove("hidden")
    this.searchButtonTextTarget.textContent = placeholder
    this.searchInputTarget.placeholder = placeholder
    this.searchInputTarget.value = ""
    this.showMessage(this.startTypingValue)

    // The dropdown controller on the search container is initialised alongside
    // this one, so open it on the next tick to be sure it has connected.
    setTimeout(() => {
      const dropdown = this.dropdownFor(this.searchContainerTarget)
      if (dropdown && dropdown.menuTarget) {
        dropdown.menuTarget.classList.remove("hidden")
        document.addEventListener("click", dropdown.boundClose)
        this.searchInputTarget.focus()
      }
    }, 50)
  }

  search(event) {
    const query = event.target.value.trim()

    if (query.length < 2) {
      this.showMessage(this.startTypingValue)
      return
    }

    const filter = this.constructor.FILTERS[this.currentFilterType]
    if (!filter) return

    this.showMessage(this.searchingValue)

    // Only the newest keystroke gets to paint results - a slow earlier request
    // must not overwrite them when it lands.
    const requestId = (this.requestId || 0) + 1
    this.requestId = requestId

    fetch(`${filter.url}?query=${encodeURIComponent(query)}`, {
      headers: { "Accept": "application/json", "X-Requested-With": "XMLHttpRequest" }
    })
      .then((response) => response.json())
      .then((data) => {
        if (requestId !== this.requestId) return
        this.displayResults(data)
      })
      .catch(() => {
        if (requestId !== this.requestId) return
        this.showMessage(this.errorValue, "text-red-500")
      })
  }

  displayResults(data) {
    if (!data || data.length === 0) {
      this.showMessage(this.noResultsValue)
      return
    }

    const filter = this.constructor.FILTERS[this.currentFilterType]

    this.resultsTarget.innerHTML = data.map((item) => {
      const name = this.displayName(item)
      const value = item[filter.value]

      return `
        <a href="#" class="block px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
           data-action="click->catalog-filter#selectResult"
           data-filter-value="${this.escape(String(value))}">
          <div class="flex items-center gap-3">
            ${this.thumbnail(item, name)}
            <div class="flex-1 min-w-0">
              <div class="text-sm font-medium text-gray-900 truncate">${this.escape(name)}</div>
              ${this.subtitle(item)}
            </div>
          </div>
        </a>
      `
    }).join("")
  }

  selectResult(event) {
    event.preventDefault()

    const filter = this.constructor.FILTERS[this.currentFilterType]
    if (!filter) return

    const url = new URL(window.location.href)
    url.searchParams.set(filter.param, event.currentTarget.dataset.filterValue)
    // A new filter means a different result set, so the old page number would
    // land on an empty grid.
    url.searchParams.delete("page")

    Turbo.visit(url.toString())
  }

  displayName(item) {
    return item.name || item.title || item.label_name || ""
  }

  subtitle(item) {
    if (item.album_count === undefined || item.album_count === null) return ""

    return `<div class="text-xs text-gray-500 tabular-nums">${item.album_count} ${this.escape(this.albumsLabelValue)}</div>`
  }

  thumbnail(item, name) {
    if (item.spotify_thumbnail_uri) {
      return `<img src="${this.escape(item.spotify_thumbnail_uri)}" alt="${this.escape(name)}" class="w-10 h-10 rounded object-cover flex-shrink-0">`
    }

    return `
      <div class="w-10 h-10 rounded bg-gray-200 flex items-center justify-center flex-shrink-0">
        <svg class="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      </div>
    `
  }

  showMessage(message, colorClass = "text-gray-500") {
    this.resultsTarget.innerHTML = `<div class="px-4 py-3 text-sm ${colorClass}">${this.escape(message)}</div>`
  }

  // Names come from user data and are interpolated into markup, so quotes and
  // angle brackets have to be neutralised before they reach innerHTML.
  escape(value) {
    const element = document.createElement("div")
    element.textContent = value
    return element.innerHTML.replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }

  dropdownFor(element) {
    return this.application.getControllerForElementAndIdentifier(element, "dropdown")
  }

  closeDropdownFor(element) {
    const dropdown = this.dropdownFor(element)
    if (dropdown) dropdown.close()
  }
}
